import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import AdForgeLogo from './AdForgeLogo';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line animate-spin text-2xl text-orange-500" />
          <span className="text-sm text-stone-500">Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const isEmailVerified = !!user.email_confirmed_at;

  if (!isEmailVerified) {
    const handleResend = async () => {
      if (!user.email) return;
      setResendStatus('sending');
      const { error } = await supabase.auth.resend({ type: 'signup', email: user.email });
      setResendStatus(error ? 'error' : 'sent');
    };

    const handleSignOut = async () => {
      await supabase.auth.signOut();
    };

    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6">
        <div className="w-full max-w-md text-center">
          <div className="mb-6 flex justify-center">
            <AdForgeLogo size="lg" light={false} />
          </div>

          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-orange-100 mx-auto mb-5">
            <i className="ri-mail-send-line text-3xl text-orange-500" />
          </div>

          <h1 className="text-2xl font-extrabold text-stone-900 mb-2">Verify your email</h1>
          <p className="text-sm text-stone-500 mb-2 leading-relaxed">
            We sent a confirmation link to
          </p>
          <p className="text-sm font-semibold text-stone-800 mb-6">{user.email}</p>
          <p className="text-sm text-stone-500 mb-8 leading-relaxed">
            Click the link in that email to activate your account. Check your spam folder if you don&apos;t see it.
          </p>

          {resendStatus === 'sent' && (
            <div className="mb-5 px-4 py-3 rounded-xl bg-green-50 border border-green-100 text-green-700 text-sm flex items-center justify-center gap-2">
              <i className="ri-checkbox-circle-line" />
              Confirmation email resent! Check your inbox.
            </div>
          )}
          {resendStatus === 'error' && (
            <div className="mb-5 px-4 py-3 rounded-xl bg-rose-50 border border-rose-100 text-rose-600 text-sm">
              Something went wrong. Please try again.
            </div>
          )}

          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={handleResend}
              disabled={resendStatus === 'sending' || resendStatus === 'sent'}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-400 text-white font-bold text-sm hover:from-orange-600 hover:to-amber-500 transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
            >
              {resendStatus === 'sending' ? (
                <><i className="ri-loader-4-line animate-spin" /> Sending...</>
              ) : resendStatus === 'sent' ? (
                <><i className="ri-check-line" /> Email sent!</>
              ) : (
                <><i className="ri-mail-send-line" /> Resend confirmation email</>
              )}
            </button>

            <button
              type="button"
              onClick={handleSignOut}
              className="w-full py-3 rounded-xl border border-stone-200 text-stone-600 text-sm font-semibold hover:bg-stone-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Sign out
            </button>
          </div>

          <p className="mt-6 text-xs text-stone-400">
            Wrong email?{' '}
            <button type="button" onClick={handleSignOut} className="text-orange-500 hover:underline cursor-pointer">
              Sign up again
            </button>
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
